import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { skillsData, skillCategories, skillStats, type Skill } from '@/mocks/skillsData';

function formatDownloads(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return `${n}`;
}

function SkillCard({ skill, index, matchedField }: { skill: Skill; index: number; matchedField?: string }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#999]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#666]' : 'text-[#888]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="glass-card-hover p-5 group flex flex-col h-full"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`text-base font-semibold truncate ${textPrimary} group-hover:text-[#00ff88] transition-colors`}>
              {skill.name}
            </h3>
          </div>
          <p className={`text-xs mt-0.5 ${textMuted}`}>
            by {skill.author}
          </p>
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider border ${
          skill.tag === 'Plugin'
            ? 'border-[#00ccff]/30 text-[#00ccff] bg-[#00ccff]/10'
            : 'border-[#00ff88]/30 text-[#00ff88] bg-[#00ff88]/10'
        }`}>
          {skill.tag}
        </span>
      </div>

      {/* Description */}
      <p className={`text-sm leading-relaxed line-clamp-3 mb-4 flex-1 ${textSecondary}`}>
        {skill.description}
      </p>

      {/* Footer */}
      <div className={`pt-3 border-t flex items-center justify-between ${borderLight}`}>
        <div className={`flex items-center gap-3 text-xs font-mono ${textMuted}`}>
          <span className="flex items-center gap-1">
            <i className="ri-star-line w-3 h-3 flex items-center justify-center" />
            {skill.stars}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-download-line w-3 h-3 flex items-center justify-center" />
            {formatDownloads(skill.downloads)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {matchedField && (
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border border-[#00ff88]/20 text-[#00ff88]/70`}>
              match: {matchedField}
            </span>
          )}
          <Link
            to={`/skills/${skill.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#00ff88]/10 border border-[#00ff88]/30 text-[#00ff88] text-xs font-medium rounded-lg hover:bg-[#00ff88]/20 transition-all whitespace-nowrap"
          >
            <i className="ri-download-line w-3 h-3 flex items-center justify-center" />
            {t('skills.install', 'Install')}
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default function SkillsListPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const allSkills = skillsData;

  const dedupedSkills = useMemo(() => {
    const seen = new Set<string>();
    return allSkills.filter((skill) => {
      if (seen.has(skill.id)) return false;
      seen.add(skill.id);
      return true;
    });
  }, [allSkills]);

  const featuredSkills = dedupedSkills.filter((s) => s.featured);
  const trendingSkills = dedupedSkills.filter((s) => s.trending);

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#999]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#666]' : 'text-[#888]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const bgWhite5 = isDark ? 'bg-white/5' : 'bg-black/5';

  interface SkillMatch {
    skill: Skill;
    score: number;
    matchedField: string;
  }

  const searchResults = useMemo<SkillMatch[]>(() => {
    const q = searchQuery.toLowerCase().trim();

    if (!q || q.length < 2) {
      return dedupedSkills
        .filter((skill) => activeCategory === 'all' || skill.category === activeCategory)
        .map((skill, i) => ({ skill, score: 0, matchedField: '' }));
    }

    const matches: SkillMatch[] = [];

    for (const skill of dedupedSkills) {
      if (activeCategory !== 'all' && skill.category !== activeCategory) continue;

      const id = (skill.id || '').toLowerCase();
      const name = (skill.name || '').toLowerCase();
      const author = (skill.author || '').toLowerCase();
      const authorHandle = (skill.authorHandle || '').toLowerCase();
      const desc = (skill.description || '').toLowerCase();
      const detailedDesc = (skill.detailedDescription || '').toLowerCase();

      let score = 0;
      let matchedField = '';

      if (id === q) {
        score = 100; matchedField = 'id-exact';
      } else if (name === q) {
        score = 95; matchedField = 'name-exact';
      } else if (id.startsWith(q)) {
        score = 90; matchedField = 'id-prefix';
      } else if (name.startsWith(q)) {
        score = 85; matchedField = 'name-prefix';
      } else if (id.includes(q)) {
        score = 80; matchedField = 'id';
      } else if (name.includes(q)) {
        score = 75; matchedField = 'name';
      } else if (author.includes(q)) {
        score = 50; matchedField = 'author';
      } else if (authorHandle.includes(q)) {
        score = 45; matchedField = 'authorHandle';
      } else if (desc.includes(q)) {
        score = 30; matchedField = 'description';
      } else if (detailedDesc.includes(q)) {
        score = 20; matchedField = 'detailedDescription';
      }

      if (score > 0) {
        matches.push({ skill, score, matchedField });
      }
    }

    matches.sort((a, b) => b.score - a.score);
    return matches;
  }, [searchQuery, activeCategory, dedupedSkills]);

  const filteredSkills = searchResults.map((m) => m.skill);
  const skillMatches = searchResults;

  const suggestions = [
    'dexscreener',
    'jupiter swap',
    'aerodrome',
    'farcaster',
    'hyperliquid',
  ];

  return (
    <div className={`relative min-h-screen overflow-x-hidden ${isDark ? 'bg-[#050505] text-white' : 'bg-[#f5f5f5] text-[#111]'}`}>
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className={`absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full blur-[150px] ${isDark ? 'bg-[#00ff88]/[0.02]' : 'bg-[#00ff88]/[0.04]'}`} />
        <div className={`absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[120px] ${isDark ? 'bg-[#00ff88]/[0.015]' : 'bg-[#00ff88]/[0.03]'}`} />
      </div>

      <Navbar />

      <main className="relative z-10 pt-24 md:pt-32 pb-16">
        {/* Hero Search */}
        <div className="w-full px-4 md:px-8 lg:px-12 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs text-[#00ff88] font-mono uppercase tracking-widest mb-4 block">
              {t('skills.builtByCommunity', 'Built by the community')}
            </span>
            <h1 className={`text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 ${textPrimary}`}>
              <span className="gradient-text">{t('skills.equip', 'Equip')}</span>
              <span className="mx-2">·</span>
              <span className="gradient-text">{t('skills.install', 'Install')}</span>
              <span className="mx-2">·</span>
              <span className="gradient-text">{t('skills.unleash', 'Unleash')}</span>
            </h1>
            <p className={`text-sm md:text-base mb-8 ${textSecondary}`}>
              {t('skills.toolsReady', 'Tools built by thousands, ready in one search.')}
            </p>

            {/* Search bar */}
            <div className={`relative max-w-2xl mx-auto mb-6 ${bgWhite5} border ${borderLight} rounded-xl overflow-hidden`}>
              <div className="flex items-center px-4 py-3">
                <i className="ri-search-line w-5 h-5 flex items-center justify-center text-[#00ff88] mr-3" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('skills.searchPlaceholder', 'What are you looking for?')}
                  className={`flex-1 bg-transparent text-sm outline-none placeholder:${textMuted} ${textPrimary}`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`ml-2 ${textMuted} hover:text-[#00ff88] transition-colors`}
                  >
                    <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
                  </button>
                )}
              </div>
            </div>

            {/* Suggestions */}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => setSearchQuery(s)}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${borderLight} ${textSecondary} hover:border-[#00ff88]/30 hover:text-[#00ff88] whitespace-nowrap`}
                >
                  {s}
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Category Tabs */}
        <div className="w-full px-4 md:px-8 lg:px-12 mb-8">
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {skillCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 text-sm rounded-lg border transition-all whitespace-nowrap ${
                  activeCategory === cat.key
                    ? 'bg-[#00ff88]/10 border-[#00ff88]/30 text-[#00ff88] font-medium'
                    : `${borderLight} ${textSecondary} hover:border-[#00ff88]/20 hover:text-[#00ff88]`
                }`}
              >
                {t(`skills.categories.${cat.key}`, cat.label)}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="w-full px-4 md:px-8 lg:px-12 mb-12">
          <div className={`flex flex-wrap items-center justify-center gap-6 md:gap-10 py-6 border-y ${borderLight}`}>
            {[
              { num: skillStats.totalTools, label: t('skills.stats.tools', 'tools') },
              { num: skillStats.totalUsers, label: t('skills.stats.users', 'users') },
              { num: skillStats.totalDownloads, label: t('skills.stats.downloads', 'downloads') },
              { num: skillStats.avgRating, label: t('skills.stats.rating', 'avg rating') },
            ].map((stat) => (
              <div key={stat.label} className="flex items-baseline gap-1.5">
                <span className={`text-xl md:text-2xl font-bold font-mono ${textPrimary}`}>{stat.num}</span>
                <span className={`text-xs ${textMuted}`}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Featured Skills */}
        {activeCategory === 'all' && !searchQuery && featuredSkills.length > 0 && (
          <div className="w-full px-4 md:px-8 lg:px-12 mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-lg md:text-xl font-semibold ${textPrimary}`}>
                {t('skills.featuredSkills', 'Featured Skills')}
              </h2>
              <Link
                to="/skills"
                className="text-xs text-[#00ff88] hover:underline flex items-center gap-1"
                onClick={() => setActiveCategory('all')}
              >
                {t('skills.viewAll', 'View all')}
                <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {featuredSkills.map((skill, i) => (
                <SkillCard key={skill.id} skill={skill} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* Trending Skills */}
        {activeCategory === 'all' && !searchQuery && trendingSkills.length > 0 && (
          <div className="w-full px-4 md:px-8 lg:px-12 mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-lg md:text-xl font-semibold ${textPrimary}`}>
                {t('skills.trendingNow', 'Trending Now')}
              </h2>
              <Link
                to="/skills"
                className="text-xs text-[#00ff88] hover:underline flex items-center gap-1"
                onClick={() => setActiveCategory('all')}
              >
                {t('skills.viewAll', 'View all')}
                <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trendingSkills.map((skill, i) => (
                <SkillCard key={skill.id} skill={skill} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* All Skills / Search Results */}
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-lg md:text-xl font-semibold ${textPrimary}`}>
              {searchQuery
                ? t('skills.searchResults', 'Search Results')
                : t('skills.allSkills', 'All Skills')}
              <span className={`ml-2 text-sm font-normal ${textMuted}`}>
                ({filteredSkills.length})
              </span>
            </h2>
          </div>

          {filteredSkills.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-search-line w-10 h-10 flex items-center justify-center mx-auto mb-3 text-2xl text-[#00ff88]/50" />
              <p className={`text-sm ${textSecondary}`}>{t('skills.noResults', 'No skills found matching your criteria.')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {skillMatches.map((match, i) => (
                <SkillCard key={match.skill.id} skill={match.skill} index={i} matchedField={match.matchedField} />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}